import React, { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Filter, Grid3X3, List, Star, BadgeCheck, SlidersHorizontal, X, ChevronDown, GitCompare, Check, Loader2 } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { useTools } from '../hooks/useData';
import { useCompare } from '../context/CompareContext';
import { toast } from 'sonner';

const ToolsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('popular');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [priceFilter, setPriceFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const { tools, loading, categories } = useTools();
  const { toggleCompare, isInCompare, compareCount } = useCompare();

  const handleToggleCompare = (e, tool) => {
    e.preventDefault();
    e.stopPropagation();
    const result = toggleCompare(tool);
    if (result.success) {
      toast.success(result.message);
    } else {
      toast.error(result.message);
    }
  };

  const filteredTools = useMemo(() => {
    let result = [...tools];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (tool) =>
          tool.name.toLowerCase().includes(query) ||
          tool.description.toLowerCase().includes(query) ||
          tool.category.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (selectedCategories.length > 0) {
      result = result.filter((tool) =>
        selectedCategories.includes(tool.categorySlug)
      );
    }

    // Price filter
    if (priceFilter !== 'all') {
      result = result.filter((tool) => tool.priceType === priceFilter);
    }

    // Sorting
    switch (sortBy) {
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'reviews':
        result.sort((a, b) => (b.reviews || 0) - (a.reviews || 0));
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'newest':
        result.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        break;
      default:
        // popular - keep trending items first
        result.sort((a, b) => (b.trending ? 1 : 0) - (a.trending ? 1 : 0));
    }

    return result;
  }, [tools, searchQuery, selectedCategories, priceFilter, sortBy]);

  const toggleCategory = (slug) => {
    setSelectedCategories((prev) =>
      prev.includes(slug)
        ? prev.filter((c) => c !== slug)
        : [...prev, slug]
    );
  };

  const clearFilters = () => {
    setSelectedCategories([]);
    setPriceFilter('all');
    setSearchQuery('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8 flex items-start justify-between">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
                Discover Software Tools
              </h1>
              <p className="text-lg text-gray-600">
                Browse our collection of {tools.length}+ verified business tools
              </p>
            </div>
            {compareCount > 0 && (
              <Link to="/compare">
                <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="go-to-compare-btn">
                  <GitCompare className="w-4 h-4 mr-2" />
                  Compare ({compareCount})
                </Button>
              </Link>
            )}
          </div>

          {/* Search and Filter Bar */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-8">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search tools..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 py-6 bg-gray-50 border-0"
                  data-testid="tools-search-input"
                />
              </div>

              {/* Sort */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full lg:w-48 py-6 bg-gray-50 border-0">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="popular">Most Popular</SelectItem>
                  <SelectItem value="rating">Highest Rated</SelectItem>
                  <SelectItem value="reviews">Most Reviews</SelectItem>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="name">Alphabetical</SelectItem>
                </SelectContent>
              </Select>

              {/* Filter Toggle (Mobile) */}
              <Button
                variant="outline"
                className="lg:hidden"
                onClick={() => setShowFilters(!showFilters)}
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Filters
                {(selectedCategories.length > 0 || priceFilter !== 'all') && (
                  <Badge className="ml-2 bg-emerald-500">
                    {selectedCategories.length + (priceFilter !== 'all' ? 1 : 0)}
                  </Badge>
                )}
              </Button>

              {/* View Mode */}
              <div className="hidden lg:flex items-center gap-1 bg-gray-50 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Grid3X3 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'list'
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex gap-8">
            {/* Sidebar Filters */}
            <aside
              className={`${
                showFilters ? 'fixed inset-0 z-50 bg-white p-6 overflow-auto' : 'hidden'
              } lg:block lg:relative lg:w-64 lg:flex-shrink-0`}
            >
              <div className="lg:sticky lg:top-24">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-semibold text-gray-900">Filters</h3>
                  <div className="flex items-center gap-2">
                    {(selectedCategories.length > 0 || priceFilter !== 'all') && (
                      <button
                        onClick={clearFilters}
                        className="text-sm text-emerald-600 hover:text-emerald-700"
                      >
                        Clear all
                      </button>
                    )}
                    <button
                      className="lg:hidden"
                      onClick={() => setShowFilters(false)}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Categories */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Categories
                  </h4>
                  <div className="space-y-2">
                    {categories.map((category) => {
                      // Calculate actual tool count for this category
                      const actualCount = tools.filter(t => t.categorySlug === category.slug).length;
                      return (
                        <label
                          key={category.id}
                          className="flex items-center gap-3 cursor-pointer group"
                        >
                          <Checkbox
                            checked={selectedCategories.includes(category.slug)}
                            onCheckedChange={() => toggleCategory(category.slug)}
                          />
                          <span className="text-sm text-gray-600 group-hover:text-gray-900">
                            {category.name}
                          </span>
                          <span className="text-xs text-gray-400 ml-auto">
                            {actualCount}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Price Type */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Pricing
                  </h4>
                  <div className="space-y-2">
                    {[
                      { value: 'all', label: 'All' },
                      { value: 'freemium', label: 'Freemium' },
                      { value: 'paid', label: 'Paid Only' },
                      { value: 'free', label: 'Free' },
                      { value: 'custom', label: 'Custom Pricing' },
                    ].map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center gap-3 cursor-pointer group"
                      >
                        <Checkbox
                          checked={priceFilter === option.value}
                          onCheckedChange={() => setPriceFilter(option.value)}
                        />
                        <span className="text-sm text-gray-600 group-hover:text-gray-900">
                          {option.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Apply Button (Mobile) */}
                <Button
                  className="w-full lg:hidden bg-emerald-500 hover:bg-emerald-600"
                  onClick={() => setShowFilters(false)}
                >
                  Apply Filters
                </Button>
              </div>
            </aside>

            {/* Tools Grid */}
            <div className="flex-1">
              {/* Results Count */}
              <div className="mb-4 text-sm text-gray-500">
                Showing {filteredTools.length} tools
                {searchQuery && ` for "${searchQuery}"`}
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-20 bg-white rounded-2xl border border-gray-100">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                  <span className="ml-2 text-gray-600">Loading tools...</span>
                </div>
              ) : filteredTools.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No tools found
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Try adjusting your search or filters
                  </p>
                  <Button variant="outline" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredTools.map((tool) => (
                    <div
                      key={tool.id}
                      className="group bg-white rounded-2xl border border-gray-100 p-5 hover:border-gray-200 hover:shadow-xl hover:shadow-gray-100/50 transition-all duration-300 relative"
                    >
                      <Link to={`/tool/${tool.slug}`}>
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-14 h-14 rounded-xl bg-gray-100 overflow-hidden">
                            <img
                              src={tool.logo}
                              alt={tool.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/100?text=' + tool.name.charAt(0);
                              }}
                            />
                          </div>
                          {tool.verified && (
                            <Badge
                              variant="secondary"
                              className="bg-emerald-50 text-emerald-600 border-0"
                            >
                              <BadgeCheck className="w-3 h-3 mr-1" />
                              Verified
                            </Badge>
                          )}
                        </div>

                        <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-emerald-600 transition-colors">
                          {tool.name}
                        </h3>
                        <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                          {tool.description}
                        </p>

                        <div className="mb-4">
                          <span className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded-md">
                            {tool.category}
                          </span>
                        </div>

                        <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                            <span className="text-sm font-medium text-gray-900">
                              {tool.rating}
                            </span>
                            <span className="text-xs text-gray-400">
                              ({(tool.reviews || 0).toLocaleString()})
                            </span>
                          </div>
                          <span className="text-sm font-medium text-emerald-600">
                            {tool.pricing}
                          </span>
                        </div>
                      </Link>
                      
                      {/* Add to Compare Button */}
                      <button
                        onClick={(e) => handleToggleCompare(e, tool)}
                        className={`absolute top-4 right-4 p-2 rounded-lg transition-all ${
                          isInCompare(tool.id)
                            ? 'bg-emerald-500 text-white'
                            : 'bg-gray-100 text-gray-500 hover:bg-emerald-100 hover:text-emerald-600 opacity-0 group-hover:opacity-100'
                        }`}
                        title={isInCompare(tool.id) ? 'Remove from Compare' : 'Add to Compare'}
                        data-testid={`compare-btn-${tool.slug}`}
                      >
                        {isInCompare(tool.id) ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <GitCompare className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredTools.map((tool) => (
                    <div
                      key={tool.id}
                      className="group flex gap-6 bg-white rounded-2xl border border-gray-100 p-6 hover:border-gray-200 hover:shadow-xl hover:shadow-gray-100/50 transition-all duration-300 relative"
                    >
                      <Link to={`/tool/${tool.slug}`} className="flex gap-6 flex-1">
                        <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0">
                          <img
                            src={tool.logo}
                            alt={tool.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/100?text=' + tool.name.charAt(0);
                            }}
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors">
                              {tool.name}
                            </h3>
                            {tool.verified && (
                              <Badge
                                variant="secondary"
                                className="bg-emerald-50 text-emerald-600 border-0 flex-shrink-0"
                              >
                                <BadgeCheck className="w-3 h-3 mr-1" />
                                Verified
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mb-3">
                            {tool.description}
                          </p>
                          <div className="flex items-center gap-4">
                            <span className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded-md">
                              {tool.category}
                            </span>
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                              <span className="text-sm font-medium text-gray-900">
                                {tool.rating}
                              </span>
                              <span className="text-xs text-gray-400">
                                ({(tool.reviews || 0).toLocaleString()})
                              </span>
                            </div>
                            <span className="text-sm font-medium text-emerald-600">
                              {tool.pricing}
                            </span>
                          </div>
                        </div>
                      </Link>
                      
                      {/* Add to Compare Button */}
                      <button
                        onClick={(e) => handleToggleCompare(e, tool)}
                        className={`flex-shrink-0 px-4 py-2 rounded-lg transition-all flex items-center gap-2 ${
                          isInCompare(tool.id)
                            ? 'bg-emerald-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-emerald-100 hover:text-emerald-600'
                        }`}
                        data-testid={`compare-btn-list-${tool.slug}`}
                      >
                        {isInCompare(tool.id) ? (
                          <>
                            <Check className="w-4 h-4" />
                            Added
                          </>
                        ) : (
                          <>
                            <GitCompare className="w-4 h-4" />
                            Compare
                          </>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ToolsPage;
